import { useCallback, useEffect, useState } from "react";
import IntelexFrame from "../intelex/IntelexFrame";
import { confirmAction } from "../intelex/ConfirmationDialog";
import { dataList, displayDate, intelexRequest } from "../intelex/api";

function AdminSettings({ user }) {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [testLoginEnabled, setTestLoginEnabled] = useState(false);
  const [draft, setDraft] = useState({
    username: "TESTPILOT",
    firstName: "Test",
    lastName: "Pilot",
    email: "test@vwzz.eu",
    password: "",
  });
  const [state, setState] = useState({
    loading: true,
    working: "",
    error: "",
    success: "",
  });

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    if (role) params.set("role", role);
    try {
      const payload = await intelexRequest(`/api/portal/admin/users?${params}`);
      setUsers(dataList(payload));
      setTestLoginEnabled(Boolean(payload.testLoginEnabled));
      setState((current) => ({
        ...current,
        loading: false,
        working: "",
        error: "",
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        loading: false,
        working: "",
        error: error.message || "Portal users could not be loaded.",
      }));
    }
  }, [query, role]);

  useEffect(() => {
    const timer = window.setTimeout(load, query ? 220 : 0);
    return () => window.clearTimeout(timer);
  }, [load, query]);
  const notify = (success) =>
    setState({ loading: false, working: "", error: "", success });
  const changeRole = async (account, nextRole) => {
    if (
      !(await confirmAction({
        title: "Change account role",
        message: `Change ${account.username} from ${account.role} to ${nextRole}? Their other sessions may be signed out.`,
        confirmLabel: "Change role",
      }))
    )
      return;
    setState((current) => ({
      ...current,
      working: `role-${account.id}`,
      error: "",
      success: "",
    }));
    try {
      await intelexRequest(`/api/portal/admin/users/${account.id}/role`, {
        method: "PUT",
        body: { role: nextRole },
      });
      await load();
      notify(`${account.username} is now ${nextRole}.`);
    } catch (error) {
      setState((current) => ({
        ...current,
        working: "",
        error: error.message,
        success: "",
      }));
    }
  };
  const toggleAccess = async (account) => {
    const revoked = !account.accessRevoked;
    if (
      !(await confirmAction({
        title: `${revoked ? "Remove" : "Restore"} portal access`,
        message: `${revoked ? "Remove" : "Restore"} portal access for ${account.username}?${revoked ? " All active sessions will be ended." : ""}`,
        confirmLabel: revoked ? "Remove access" : "Restore access",
        tone: revoked ? "danger" : "default",
      }))
    )
      return;
    setState((current) => ({
      ...current,
      working: `access-${account.id}`,
      error: "",
      success: "",
    }));
    try {
      await intelexRequest(`/api/portal/admin/users/${account.id}/access`, {
        method: "PUT",
        body: { revoked },
      });
      await load();
      notify(
        `${account.username} access was ${revoked ? "removed" : "restored"}.`,
      );
    } catch (error) {
      setState((current) => ({
        ...current,
        working: "",
        error: error.message,
        success: "",
      }));
    }
  };
  const deleteUser = async (account) => {
    if (
      !(await confirmAction({
        title: "Delete test account",
        message: `Permanently delete unused test account ${account.username}? This cannot be undone.`,
        confirmLabel: "Delete account",
        tone: "danger",
      }))
    )
      return;
    setState((current) => ({
      ...current,
      working: `delete-${account.id}`,
      error: "",
      success: "",
    }));
    try {
      await intelexRequest(`/api/portal/admin/users/${account.id}`, {
        method: "DELETE",
      });
      await load();
      notify(`${account.username} was permanently deleted.`);
    } catch (error) {
      setState((current) => ({
        ...current,
        working: "",
        error: error.message,
        success: "",
      }));
    }
  };
  const createTest = async (event) => {
    event.preventDefault();
    setState((current) => ({
      ...current,
      working: "create",
      error: "",
      success: "",
    }));
    try {
      await intelexRequest("/api/portal/admin/users/test", {
        method: "POST",
        body: draft,
      });
      setDraft({
        username: "TESTPILOT",
        firstName: "Test",
        lastName: "Pilot",
        email: "test@vwzz.eu",
        password: "",
      });
      setShowCreate(false);
      await load();
      notify("The test pilot account is ready to use.");
    } catch (error) {
      setState((current) => ({
        ...current,
        working: "",
        error: error.message,
        success: "",
      }));
    }
  };

  if (user.role !== "admin")
    return (
      <div className="intelex-main">
        <div className="intelex-alert">Administrator access is required.</div>
      </div>
    );
  return (
    <div className="intelex-main intelex-user-admin-page">
      <header className="intelex-page-heading">
        <div>
          <h1>Access settings</h1>
          <p>
            Manage portal access, administrator roles and local test pilots.
          </p>
        </div>
        <button
          type="button"
          className="intelex-primary-button"
          onClick={() => setShowCreate((value) => !value)}
        >
          {showCreate ? "Close" : "Create test account"}
        </button>
      </header>
      {state.error && (
        <div className="intelex-alert" role="alert">
          {state.error}
        </div>
      )}
      {state.success && (
        <div className="intelex-success" role="status">
          {state.success}
        </div>
      )}
      {showCreate && (
        <form
          className="intelex-panel intelex-test-account-form"
          onSubmit={createTest}
        >
          <div className="intelex-panel-heading">
            <div>
              <h2>New test pilot</h2>
              <span>Entry exam and published report forms only</span>
            </div>
          </div>
          {!testLoginEnabled && (
            <div className="intelex-alert">
              Test sign-in is disabled. Set INTEREP_TEST_LOGIN_ENABLED=true on
              the backend before creating an account.
            </div>
          )}
          <div>
            <label>
              <span>Username</span>
              <input
                required
                pattern="[A-Za-z0-9_-]{3,30}"
                value={draft.username}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    username: event.target.value.toUpperCase(),
                  }))
                }
              />
            </label>
            <label>
              <span>First name</span>
              <input
                required
                value={draft.firstName}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    firstName: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              <span>Last name</span>
              <input
                required
                value={draft.lastName}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    lastName: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              <span>Email</span>
              <input
                required
                type="email"
                value={draft.email}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
              />
            </label>
            <label className="password">
              <span>Temporary password</span>
              <input
                required
                type="password"
                minLength="12"
                maxLength="200"
                autoComplete="new-password"
                value={draft.password}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    password: event.target.value,
                  }))
                }
              />
              <small>
                Use at least 12 characters and share it through a private
                channel.
              </small>
            </label>
          </div>
          <footer>
            <button
              type="button"
              className="intelex-secondary-button"
              onClick={() => setShowCreate(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="intelex-primary-button"
              disabled={!testLoginEnabled || state.working === "create"}
            >
              {state.working === "create" ? "Creating..." : "Create account"}
            </button>
          </footer>
        </form>
      )}
      <section className="intelex-panel">
        <div className="intelex-panel-heading">
          <div>
            <h2>Portal users</h2>
            <span>{users.length} shown</span>
          </div>
        </div>
        <div className="intelex-user-filters">
          <label>
            <span>Search users</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Callsign, name or email"
            />
          </label>
          <label>
            <span>Role</span>
            <select
              value={role}
              onChange={(event) => setRole(event.target.value)}
            >
              <option value="">All roles</option>
              <option value="admin">Administrators</option>
              <option value="staff">Staff</option>
              <option value="pilot">Pilots</option>
              <option value="applicant">Applicants</option>
              <option value="test">Test accounts</option>
            </select>
          </label>
        </div>
        {state.loading ? (
          <div className="intelex-loading">Loading users...</div>
        ) : users.length ? (
          <div className="intelex-user-list">
            {users.map((account) => (
              <article
                key={account.id}
                className={account.accessRevoked ? "is-revoked" : ""}
              >
                <div className="intelex-user-identity">
                  <span className="intelex-user-avatar">
                    {String(account.username || "?").slice(0, 2)}
                  </span>
                  <div>
                    <h3>{account.username}</h3>
                    <p>
                      {`${account.firstName || ""} ${account.lastName || ""}`.trim() ||
                        "No name"}{" "}
                      · {account.email || "No email"}
                    </p>
                    <small>
                      {account.isTestAccount
                        ? "Local test account"
                        : "vAMSYS account"}{" "}
                      · Last login {displayDate(account.lastLoginAt)}
                    </small>
                  </div>
                </div>
                <dl>
                  <div>
                    <dt>Reports</dt>
                    <dd>{account.reportCount}</dd>
                  </div>
                  <div>
                    <dt>Exam attempts</dt>
                    <dd>{account.attemptCount}</dd>
                  </div>
                  <div>
                    <dt>Access</dt>
                    <dd>{account.accessRevoked ? "Removed" : "Active"}</dd>
                  </div>
                </dl>
                <div className="intelex-user-actions">
                  {account.isTestAccount ? (
                    <span className="intelex-role-label">Test pilot</span>
                  ) : (
                    <select
                      aria-label={`Role for ${account.username}`}
                      value={account.role}
                      disabled={
                        account.id === user.id ||
                        account.protectedAdmin ||
                        state.working === `role-${account.id}`
                      }
                      onChange={(event) =>
                        changeRole(account, event.target.value)
                      }
                    >
                      <option value="pilot">Pilot</option>
                      <option value="staff">Staff</option>
                      <option value="admin">Administrator</option>
                      {account.role === "applicant" && (
                        <option value="applicant">Applicant</option>
                      )}
                    </select>
                  )}
                  <button
                    type="button"
                    disabled={
                      account.id === user.id ||
                      account.protectedAdmin ||
                      Boolean(state.working)
                    }
                    onClick={() => toggleAccess(account)}
                  >
                    {account.accessRevoked ? "Restore access" : "Remove access"}
                  </button>
                  {account.isTestAccount &&
                    account.reportCount === 0 &&
                    account.attemptCount === 0 && (
                      <button
                        type="button"
                        className="danger"
                        disabled={Boolean(state.working)}
                        onClick={() => deleteUser(account)}
                      >
                        Delete
                      </button>
                    )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="intelex-empty">
            <strong>No users match these filters</strong>
          </div>
        )}
      </section>
    </div>
  );
}

export default function InterepAdminSettingsPage() {
  return (
    <IntelexFrame active="settings" title="Access settings">
      {({ user }) => <AdminSettings user={user} />}
    </IntelexFrame>
  );
}
